import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api/baseQuery';
import type { Claim, ClaimDetail, ClaimFacets, ClaimStatus, PaginatedResponse } from '@core/types/api';

/** A page of billings as the list consumes it. */
export interface ClaimPage {
  results: Claim[];
  count: number;
}

/** What the facets take: every filter of the list but the status, which they count. */
export interface ClaimFilters {
  insurers?: string[];
  professional?: string;
  /** The day the billing was sent, both ends inclusive (ISO). */
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export interface ListClaimsArgs extends ClaimFilters {
  page: number;
  pageSize: number;
  ordering?: string;
  /** Any of these states (repeated in the query string). */
  statuses?: ClaimStatus[];
}

/**
 * The query string of the list and of its facets.
 *
 * A filter with several values is the same key repeated, which `URLSearchParams`
 * can say and a plain object cannot.
 */
export function claimParams({ insurers, professional, dateFrom, dateTo, search }: ClaimFilters): URLSearchParams {
  const params = new URLSearchParams();
  for (const insurer of insurers ?? []) params.append('insurer', insurer);
  if (professional) params.set('professional', professional);
  if (dateFrom) params.set('date_from', dateFrom);
  if (dateTo) params.set('date_to', dateTo);
  if (search) params.set('search', search);
  return params;
}

function listParams({ page, pageSize, ordering, statuses, ...filters }: ListClaimsArgs): URLSearchParams {
  const params = claimParams(filters);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  // Newest first unless the reader sorted: the last visit billed is the one asked about.
  params.set('ordering', ordering || '-billed_at');
  for (const status of statuses ?? []) params.append('status', status);
  return params;
}

/**
 * "Facturas": every billing the counter sent to an insurer's portal, on the active
 * company, and the way to cancel one on the portal.
 */
export const claimsApi = createApi({
  reducerPath: 'claimsApi',
  baseQuery,
  tagTypes: ['Claim'],
  endpoints: (builder) => ({
    listClaims: builder.query<ClaimPage, ListClaimsArgs>({
      query: (args) => ({ url: `/api/claims/?${listParams(args)}`, method: 'GET' }),
      transformResponse: (response: PaginatedResponse<Claim>) => ({
        results: response?.results ?? [],
        count: response?.count ?? 0,
      }),
      providesTags: ['Claim'],
    }),

    getClaimFacets: builder.query<ClaimFacets, ClaimFilters>({
      query: (filters) => ({ url: `/api/claims/facets/?${claimParams(filters)}`, method: 'GET' }),
      providesTags: ['Claim'],
    }),

    getClaim: builder.query<ClaimDetail, string>({
      query: (uuid) => ({ url: `/api/claims/${uuid}/`, method: 'GET' }),
      providesTags: ['Claim'],
    }),

    /** Sends the cancellation to the insurer's portal; it cannot be undone. */
    cancelClaim: builder.mutation<{ claim: ClaimDetail }, string>({
      query: (uuid) => ({ url: `/api/claims/${uuid}/cancel/`, method: 'POST' }),
      invalidatesTags: ['Claim'],
    }),

    downloadDocument: builder.mutation<string, { claim: string; document: string }>({
      query: ({ claim, document }) => ({
        url: `/api/claims/${claim}/documents/${document}/download/`,
        method: 'GET',
        responseHandler: (response: Response) => response.blob(),
      }),
      transformResponse: (blob: Blob) => URL.createObjectURL(blob),
    }),
  }),
});

export const {
  useListClaimsQuery,
  useGetClaimFacetsQuery,
  useGetClaimQuery,
  useCancelClaimMutation,
  useDownloadDocumentMutation,
} = claimsApi;
